import { abbreviateDays } from './flyerDisplay';

const VOL_DAY_ORDER = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAY_RES = [
  ['Wed', /\b(wednesday|wed\.?)\b/i],
  ['Thu', /\b(thursday|thurs\.?|thur\.?|thu\.?)\b/i],
  ['Fri', /\b(friday|fri\.?)\b/i],
  ['Sat', /\b(saturday|sat\.?)\b/i],
  ['Sun', /\b(sunday|sun\.?)\b/i],
];

/**
 * Pending / Accepted / Cancelled for mentor-facing UI.
 * @param {object} flyer
 * @returns {string}
 */
export function mentorApprovalDisplayLabel(flyer) {
  if (!flyer || flyer.type !== 'mentor') return '—';
  if (flyer.cancelled) return 'Off roster';
  const st = (flyer.registrationMentorStatus || '').toString().toLowerCase();
  if (st === 'cancelled') return 'Declined';
  if (st === 'declined') return 'Declined';
  if (st === 'partial_denied') return 'Partial denial';
  if (flyer.registrationMentorManualOverride === true) return 'Accepted';
  if (flyer.registrationMentorApproved === true || st === 'approved') return 'Accepted';
  return 'Pending';
}

/**
 * @param {string[]} labels
 * @returns {string|null}
 */
export function deriveVolunteerDaySummaryFromLabels(labels) {
  const matched = new Set();
  const list = (labels || []).map((x) => String(x || '').trim()).filter(Boolean);
  if (!list.length) return null;
  for (const lab of list) {
    const s = lab.toLowerCase();
    for (const [day, re] of DAY_RES) {
      if (re.test(s)) matched.add(day);
    }
  }
  if (matched.size === 0) {
    const blob = list.map((x) => x.toLowerCase()).join(' | ');
    for (const [day, re] of DAY_RES) {
      if (re.test(blob)) matched.add(day);
    }
  }
  const ordered = VOL_DAY_ORDER.filter((d) => matched.has(d));
  return ordered.length ? ordered.join(' / ') : null;
}

/**
 * @param {object} flyer type volunteer
 */
export function volunteerDayDisplay(flyer) {
  if (!flyer || flyer.type !== 'volunteer') return '—';
  const explicit = String(flyer.volunteerDay || '').trim();
  if (explicit) return explicit;
  const labels = Array.isArray(flyer.furyOfferings)
    ? flyer.furyOfferings.map((o) => (o && o.label ? String(o.label) : '')).filter(Boolean)
    : [];
  const fromOfferings = deriveVolunteerDaySummaryFromLabels(labels);
  if (fromOfferings) return fromOfferings;
  if (labels.length) return labels.join('; ');
  return '—';
}

export function shirtSizeDisplay(flyer) {
  if (!flyer) return '—';
  const s = (flyer.tShirtSize || flyer.tShirtRequested || '').toString().trim();
  return s || '—';
}

/**
 * Event days from answers plus Fury line items as fallback.
 * @param {object} flyer type mentor
 */
export function mentorDaysWorkingDisplay(flyer) {
  if (!flyer || flyer.type !== 'mentor') return '—';
  const fromDays = abbreviateDays(flyer.days);
  if (fromDays) return fromDays;
  const fo = Array.isArray(flyer.furyOfferings) ? flyer.furyOfferings : [];
  if (fo.length) {
    return fo.map((o) => `${o.label}${o.quantity > 1 ? ` x${o.quantity}` : ''}`).join('; ');
  }
  return '—';
}

/**
 * Teams where this flyer is the assigned mentor.
 * @param {object} flyer
 * @param {{ teams: object[], asMentor?: boolean }|null|undefined} membership from FlyerDetailModal logic
 * @param {Map<string, object>} flyersByEmail lowercased email -> flyer
 * @param {(f: object) => string} getDisplayName
 */
export function mentorTeamAssignmentLines(flyer, membership, flyersByEmail, getDisplayName) {
  if (!flyer || flyer.type !== 'mentor') return [];
  const emailKey = (flyer.email || '').toLowerCase().trim();
  const lines = [];
  if (membership && Array.isArray(membership.teams)) {
    for (const team of membership.teams) {
      const mentorEmail = (team.mentor || '').toLowerCase().trim();
      if (!mentorEmail || mentorEmail !== emailKey) continue;
      const parts = [];
      if (team.teamNumber != null && String(team.teamNumber).trim() !== '') {
        parts.push(`Team #${team.teamNumber}`);
      }
      const freshlingNames = (team.freshlings || [])
        .map((e) => {
          const f = flyersByEmail.get((e || '').toLowerCase().trim());
          return f ? getDisplayName(f) : e || '';
        })
        .filter(Boolean);
      if (freshlingNames.length) parts.push(`Freshlings: ${freshlingNames.join(', ')}`);
      lines.push(parts.length ? parts.join(' — ') : 'Team (no number)');
    }
  }
  return lines;
}
